import { LanguageContext } from "../../../../context/LanguageContext";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Box, CircularProgress, Grid } from "@mui/material";
import {
  InnerWrapper,
  MainContainer,
  TitleAndActionBox,
} from "../../../common";
import AppTableFilters from "../../../common/AppTableFilters";
import { useNavigate } from "react-router-dom";
import { formatNum } from "../../../../oscar-pos-core/constants";
import { initialFilters } from "../../Account/FilterModal";
import { useAllAccountsList } from "../../../../hooks/useAccount/accountsList";
import { useAuthContext } from "../../../../context/AuthContext";
import { buildFiltersConfig } from "../../../../utils";
import { analytics, triggerLogEvent } from "../../../../config/firebase";
import { SORT } from "../../../../constants";
import PendingPayments from "./PendingPayments";
import pendingStyles from "./PendingPaymentCollection.module.css";
import walletMainStyles from "../WalletMain.module.css";

const buildPendingListFilters = (filters) => ({
  ...filters,
  amount: {
    min: filters.fromAmount || "",
    max: filters.toAmount || "",
  },
});

const PendingPaymentCollection = () => {
  const [I18n] = useContext(LanguageContext);
  const navigate = useNavigate();
  const { business } = useAuthContext();

  const [searchQ, setSearchQ] = useState("");
  const [filters, setFilters] = useState({ ...initialFilters, sort: null });
  const [pageSize, setPageSize] = useState(0);
  const [isInitalLoading, setIsInitalLoading] = useState(true);

  const filtersRef = useRef(filters);
  const pageSizeRef = useRef(pageSize);

  const {
    pendingAmountaccountList,
    AllPendingAmountaccountListCount,
    isLoading,
    handleGetPendingAmountList,
    allNonFilterItemsCount,
  } = useAllAccountsList();

  useEffect(() => {
    triggerLogEvent(analytics, "w_payments_view");
  }, []);

  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  useEffect(() => {
    pageSizeRef.current = pageSize;
  }, [pageSize]);

  const isAppliedFilter = useMemo(() => {
    const sortApplied = Boolean(filters.sort) && filters.sort !== SORT.Newest;
    const amountApplied = Boolean(filters.fromAmount || filters.toAmount);
    const searchApplied = Boolean((searchQ || "").trim());
    return sortApplied || amountApplied || searchApplied;
  }, [filters, searchQ]);

  const pendingSortOptions = useMemo(
    () => [
      { label: I18n.newest || "Newest", value: SORT.Newest },
      { label: I18n.oldest || "Oldest", value: SORT.Oldest },
      { label: I18n.amount_high_to_low || "Amount (High to Low)", value: SORT.amountHighToLow },
      { label: I18n.amount_low_to_high || "Amount (Low to High)", value: SORT.amountLowToHigh },
    ],
    [I18n],
  );

  const filtersConfig = useMemo(
    () =>
      buildFiltersConfig(["sort", "amount", "keywords"], [], null, null, I18n.search_contact, pendingSortOptions, null, null, null, I18n),
    [I18n, pendingSortOptions],
  );

  const appTableFilterState = useMemo(
    () => ({
      sort: filters.sort,
      amount: {
        min:
          filters.fromAmount != null && filters.fromAmount !== ""
            ? String(filters.fromAmount)
            : "",
        max:
          filters.toAmount != null && filters.toAmount !== ""
            ? String(filters.toAmount)
            : "",
      },
      keywords: searchQ,
    }),
    [filters, searchQ],
  );

  const appliedChips = useMemo(() => {
    const chips = [];
    if (filters.sort) {
      chips.push({
        id: "sort",
        label: "Sort",
        value: filters.sort,
        count: 1,
      });
    }
    if (filters.fromAmount || filters.toAmount) {
      chips.push({
        id: "amount",
        label: "Amount",
        value: `Rs. ${formatNum(filters.fromAmount || 0)} - ${filters.toAmount ? formatNum(filters.toAmount) : "∞"}`,
      });
    }
    if ((searchQ || "").trim()) {
      chips.push({
        id: "keywords",
        label: "Search",
        value: searchQ.trim(),
        count: 1,
      });
    }
    return chips;
  }, [filters, searchQ]);

  useEffect(() => {
    setPageSize(0);
    setIsInitalLoading(true);
    handleGetPendingAmountList({
      business,
      startPage: 0,
      endPage: 20,
      searchText: searchQ,
      filters: buildPendingListFilters(filtersRef.current),
    }).then(() => setIsInitalLoading(false));
  }, [business]);

  useEffect(() => {
    if (pageSize <= 0) return;
    handleGetPendingAmountList({
      business,
      startPage: 0,
      endPage: 20 + pageSize,
      searchText: searchQ,
      filters: buildPendingListFilters(filtersRef.current),
    });
  }, [pageSize, business]);

  const handleSetFilter = (nextFilters, searchOverride) => {
    const search =
      searchOverride !== undefined ? searchOverride : searchQ;
    if (searchOverride !== undefined) {
      setSearchQ(searchOverride);
    }
    setFilters({ ...nextFilters });
    setPageSize(0);
    setIsInitalLoading(true);
    handleGetPendingAmountList({
      business,
      startPage: 0,
      endPage: 20,
      searchText: search,
      filters: buildPendingListFilters({
        ...nextFilters,
        amount: {
          min: nextFilters.fromAmount || "",
          max: nextFilters.toAmount || "",
        },
      }),
    }).then(() => setIsInitalLoading(false));
  };

  const handleAppTableFilterChange = (key, value) => {
    triggerLogEvent(analytics, "w_payments_filter_applied");
    const current = filtersRef.current;
    const next = { ...current };
    const k = String(key).toLowerCase();

    if (k === "sort") {
      next.sort = value || SORT.Newest;
    } else if (k === "amount") {
      next.fromAmount = value?.min ?? "";
      next.toAmount = value?.max ?? "";
    } else if (k === "keywords") {
      handleSetFilter(current, value ?? "");
      return;
    }
    handleSetFilter(next);
  };

  const handleRemoveFilterChip = (chip) => {
    triggerLogEvent(analytics, "w_payments_filter_removed");
    const current = filtersRef.current;
    switch (String(chip.label).toLowerCase()) {
      case "sort":
        handleSetFilter({ ...current, sort: null });
        break;
      case "amount":
        handleSetFilter({ ...current, fromAmount: "", toAmount: "" });
        break;
      case "keywords":
      case "search":
        handleSetFilter(filtersRef.current, "");
        break;
      default:
        break;
    }
  };

  const refechList = () => {
    handleGetPendingAmountList({
      business,
      startPage: 0,
      endPage: 20 + pageSizeRef.current,
      searchText: searchQ,
      filters: buildPendingListFilters(filtersRef.current),
    });
  };

  const handleAscending = () => {
    const current = filtersRef.current;
    const nextSort = current.sort === SORT.Newest ? SORT.Oldest : SORT.Newest;
    handleSetFilter({ ...current, sort: nextSort });
  };

  return (
    <MainContainer fluid className="quickSalePage">
      <Grid container>
        <Grid item md={12}>
          <TitleAndActionBox
            data={{
              title: I18n.collect_money,
              isBack: true,
              onBack: () => navigate(-1),
              id: "pending_payment_collection",
            }}
          />
          <InnerWrapper className="innerWrapper newInnerWrapper">
            <AppTableFilters
              filtersConfig={filtersConfig}
              filterState={appTableFilterState}
              onFilterChange={handleAppTableFilterChange}
              appliedChips={appliedChips}
              onRemoveChip={handleRemoveFilterChip}
            />
            <Box className={pendingStyles.pendingListWrapper}>
              {isInitalLoading ? (
                <Box
                  className={`${walletMainStyles.transactionListLoadingBox} h-100`}
                >
                  <CircularProgress size={25} />
                </Box>
              ) : (
                <PendingPayments
                  list={pendingAmountaccountList}
                  totalCount={AllPendingAmountaccountListCount}
                  setPageSize={setPageSize}
                  filters={filters}
                  handleAscending={handleAscending}
                  isLoading={isLoading}
                  debounceSearchName={searchQ}
                  isAppliedFilter={isAppliedFilter}
                  initialFilters={{ ...initialFilters, sort: null }}
                  handleSetFilter={handleSetFilter}
                  allNonFilterItemsCount={allNonFilterItemsCount}
                  onCollectionDateUpdated={refechList}
                />
              )}
            </Box>
          </InnerWrapper>
        </Grid>
      </Grid>
    </MainContainer>
  );
};

export default PendingPaymentCollection;
